import { ComponentType, lazy } from 'react'
import { getAllLogger } from '~/logger.ts'

const log = getAllLogger('lazyWithReload')

// A single reload is enough to recover from a stale deploy. We remember that
// we already tried within this tab so a genuinely broken chunk can't put the
// page into an endless reload loop.
const RELOAD_FLAG = 'soniq:chunk-reload'

// After we deploy a new version, a user with the app already open is still
// running the old index.html. That old page points at hashed chunk files
// (e.g. account-G2kX7xp1.js) which no longer exist on the server, so the
// dynamic import for a lazily-loaded route fails.
//
// These are the messages browsers use for that failure. They differ between
// Chrome, Firefox and Safari, so we match all of them.
function isStaleChunkError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  )
}

// Drop-in replacement for React.lazy that recovers from stale deploys.
//
// When a chunk import fails because the user is on an old version of the app,
// we reload the page once to pick up the fresh assets instead of showing an
// error. If the import fails for any other reason, or a reload has already
// been attempted, the error is rethrown so the ErrorBoundary can handle it.
// The `any` here mirrors React.lazy's own signature so a component with any
// props can be lazily loaded.
export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      const module = await factory()

      // The import worked, so we are on a fresh version. Clear the guard so a
      // future deploy can trigger a reload again.
      window.sessionStorage.removeItem(RELOAD_FLAG)

      return module
    } catch (error) {
      const alreadyReloaded =
        window.sessionStorage.getItem(RELOAD_FLAG) === '1'

      if (isStaleChunkError(error) && !alreadyReloaded) {
        log.info('Stale chunk detected, reloading to load the new app version')

        // Guard against reload loops, then reload to fetch the new assets.
        window.sessionStorage.setItem(RELOAD_FLAG, '1')
        window.location.reload()

        // Keep the Suspense fallback on screen while the page reloads by
        // returning a promise that never settles.
        return new Promise<{ default: T }>(() => {})
      }

      throw error
    }
  })
}
